import React from 'react'
import dynamic from 'next/dynamic'
import { NextSeo } from 'next-seo'
import { useSelector } from 'react-redux'
import { NextPage, NextPageContext } from 'next'

import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'
import { eventsIdSelectors, eventsIdThunks } from '@ucheba/store/events/id'
import { eventsSimilarThunks } from '@ucheba/store/events/similar'
import { eventsMyThunks } from '@ucheba/store/events/my'
import { authSelectors } from '@ucheba/store/auth'
import { EColorBgText } from '@ucheba/utils/helpers/styles/variables'
import { Store } from 'redux'
import { profileSelectors, profileThunks } from '@ucheba/store/profile'
import { IEventIdPageProps } from './types'
import { headerActions } from '../../../../components/Header/store'
import { footerActions } from '../../../../components/Footer/store'
import Custom404 from '../../../404.page'
import { getCanonicalByDomain } from '../../../../../utils/helpers/getCanonicalByDomain'
import { isFinishedEvent } from '../../../../components/EventPages/bll'
import { locationNoticeActions } from '../../../../components/DefineLocationNotice/store'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('./desktop')),
  touch: dynamic(() => import('./touch')),
}

const EventIdPage: NextPage<IEventIdPageProps> = (props) => {
  const { content, notFound, canonical } = props

  const entity = useSelector(eventsIdSelectors.entity)

  if (notFound) {
    return <Custom404 />
  }

  return (
    <>
      <NextSeo
        title={content?.seo.meta.title}
        description={content?.seo.meta.description}
        openGraph={{}}
        nofollow={entity?.isFinished || !!canonical}
        noindex={entity?.isFinished || !!canonical}
        canonical={canonical}
      />

      {getIDeviceComponent(props, devicesPages)}
    </>
  )
}

EventIdPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { query, store, res } = ctx
  const { dispatch, getState } = store
  const id = query?.id
  const isAuth = authSelectors.isAuth(store.getState())

  await Promise.all([
    dispatch(eventsIdThunks.fetch({ ctx, data: { id } })),
    isAuth && dispatch<any>(eventsMyThunks.fetch({ ctx })),
    dispatch(eventsSimilarThunks.fetch({ ctx, data: { id, limit: 5 } })),
  ])

  const entity = eventsIdSelectors.entity(store.getState())

  if (!entity) {
    if (res) {
      res.statusCode = 404
    }

    return {
      notFound: true,
    }
  }

  if (entity?.branding) {
    const colorTheme = EColorBgText[entity?.branding.headerBackgroundColor || 'white']

    dispatch(headerActions.setColor(colorTheme))
    dispatch(headerActions.setFade(entity?.branding.headerBackgroundColor || 'white'))
  }

  const title = (() => {
    const institutionName =
      entity?.institution?.name || entity?.institution?.abbreviation || ''
    return `Мероприятие ${entity.name ? `${entity.name} — ` : ''}${
      institutionName ? `${institutionName} — ` : ''
    }Учеба.ру`
  })()

  const description = (() => {
    const institutionName =
      entity?.institution?.name || entity?.institution?.abbreviation || ''

    const text = `${entity?.name}${institutionName ? `, ${institutionName}` : ''}: ${
      entity.description
    }`

    if (text.length > 300) {
      return text.slice(0, 300)
    }

    return text
  })()

  const canonical = getCanonicalByDomain(ctx, entity?.domain?.slug)

  const isFinished = isFinishedEvent(entity.startAt, entity.endAt)

  const isShowItems = !isFinished && !!entity.branding

  dispatch(headerActions.toggleHeader(!isShowItems))
  dispatch(footerActions.toogleFooter(!isShowItems))
  dispatch(locationNoticeActions.toggleLocationNotice(!isShowItems))

  const profile = profileSelectors.entity(getState())

  if (!profile) {
    await dispatch(profileThunks.get({ ctx }))
  }

  return {
    content: {
      seo: {
        meta: {
          title,
          description,
        },
      },
    },
    canonical,
  }
}

export default EventIdPage
